import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryState } from 'nuqs';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, MonitorSmartphone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import type { DevicePendingResponse } from '@reflex/shared';
import { ApiError } from '@/lib/api';
import { approveDeviceAuth, getDeviceAuthPending } from '@reflex/ui/client/generated/device';

async function fetchPending(code: string): Promise<DevicePendingResponse> {
  return getDeviceAuthPending({ code }) as Promise<DevicePendingResponse>;
}

async function submitApproval(code: string, organizationId: string): Promise<void> {
  await approveDeviceAuth({ code, organizationId });
}

/**
 * Prefer the server's human-readable `error_description` (carried on the
 * `ApiError` body) over the terse machine `error` code that `request()`
 * surfaces as the message.
 */
function friendlyError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const description = err.body.error_description;
    if (typeof description === 'string' && description) return description;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Copy for the approval page, which serves two kinds of caller.
 *
 * `reflex-cli` connects a machine and sends its `hostname`; an app connects
 * itself and sends a `clientName`. Both ask for the same thing (a personal
 * API key), but the user is deciding about different subjects, so the page
 * names the subject in every line rather than saying "this machine" to
 * someone who clicked a button on a website.
 */
function connectCopy(pending: DevicePendingResponse | undefined, completed: boolean) {
  const clientName = pending?.clientName?.trim();
  if (clientName) {
    return {
      title: completed ? `${clientName} connected` : 'Connect an app',
      description: completed
        ? `${clientName} can now use your Reflex account. Return to it to continue.`
        : `${clientName} is asking to use your Reflex account. Approving creates an API key for it. Pick the organization it should work in.`,
      returnTo: `Return to ${clientName}.`,
      restart: `Restart the connection from ${clientName} to try again.`,
    };
  }
  const machineLabel = pending?.hostname ?? 'this machine';
  return {
    title: completed ? 'Machine connected' : 'Connect a machine',
    description: completed
      ? `${machineLabel} is now connected to your account. Return to your terminal to continue.`
      : `A terminal is asking to connect ${machineLabel} to your account. Pick the organization it should join.`,
    returnTo: 'Return to your terminal.',
    restart: 'Restart the connection from your terminal to try again.',
  };
}

/**
 * Browser approval page for the "connect link" (device-authorization) flow.
 *
 * A client with no credential — `reflex-cli`, or an app connecting a Reflex
 * account from its own UI — starts the flow, then opens this page with the
 * short `?code=` prefilled. The signed-in user confirms who is asking and
 * picks the organization to connect it to; the server mints a personal API
 * key bound to them and hands it back to the waiting client on its next poll.
 *
 * Gated by `AuthGate` (unlike the MCP OAuth picker): approving a device binds
 * a freshly minted credential to the caller, so we need a real web session —
 * an unauthenticated visitor is sent through login and returns here.
 */
export function ConnectDevicePage() {
  const [code] = useQueryState('code');
  const [submitting, setSubmitting] = useState(false);
  const [submittingOrgId, setSubmittingOrgId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const autoSubmitted = useRef(false);

  const query = useQuery({
    queryKey: ['device-pending', code],
    queryFn: () => fetchPending(code ?? ''),
    enabled: !!code,
    retry: false,
  });

  const handlePick = useCallback(
    async (organizationId: string) => {
      if (!code || submitting) return;
      setSubmitting(true);
      setSubmittingOrgId(organizationId);
      setSubmitError(null);
      try {
        await submitApproval(code, organizationId);
        setCompleted(true);
      } catch (err) {
        setSubmitError(friendlyError(err, 'Could not finish connecting.'));
        setSubmitting(false);
        setSubmittingOrgId(null);
      }
    },
    [code, submitting],
  );

  const organizations = useMemo(() => query.data?.organizations ?? [], [query.data]);
  const copy = connectCopy(query.data, completed);
  const loadError = query.error ? friendlyError(query.error, 'Connect link expired') : null;
  const showLoading = !!code && query.isLoading;

  // A user with exactly one organization has nothing to choose — approve it
  // for them so "connect to the appropriate account" needs zero clicks. The
  // approval is deferred out of the effect body so it doesn't setState mid-render.
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (!query.isSuccess || organizations.length !== 1) return;
    autoSubmitted.current = true;
    const onlyOrgId = organizations[0]!.id;
    const timer = setTimeout(() => void handlePick(onlyOrgId), 0);
    return () => clearTimeout(timer);
  }, [query.isSuccess, organizations, handlePick]);

  return (
    <div
      className="flex min-h-dvh items-center justify-center bg-background p-4"
      data-testid="connect-device-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex size-10 items-center justify-center rounded-lg bg-muted">
            <MonitorSmartphone className="size-5 text-muted-foreground" />
          </div>
          <PanelTitle>{copy.title}</PanelTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {completed && (
            <Alert data-testid="connect-device-success">
              <CheckCircle2 className="size-4" />
              <AlertTitle>You are all set</AlertTitle>
              <AlertDescription>
                {copy.returnTo} It should continue automatically. It is safe to close this tab.
              </AlertDescription>
            </Alert>
          )}

          {!completed && !code && (
            <Alert variant="destructive" data-testid="connect-device-missing-code">
              <AlertCircle className="size-4" />
              <AlertTitle>Missing code</AlertTitle>
              <AlertDescription>
                This link is missing its connect code. {copy.restart}
              </AlertDescription>
            </Alert>
          )}

          {!completed && showLoading && (
            <p className="text-center text-sm text-muted-foreground">Loading organizations...</p>
          )}

          {!completed && code && loadError && (
            <Alert variant="destructive" data-testid="connect-device-load-error">
              <AlertCircle className="size-4" />
              <AlertTitle>Connect link expired</AlertTitle>
              <AlertDescription>
                {loadError} {copy.restart}
              </AlertDescription>
            </Alert>
          )}

          {!completed && code && !loadError && !showLoading && organizations.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              You are not a member of any organization yet.
            </p>
          )}

          {!completed && organizations.length > 0 && (
            <ul className="flex flex-col gap-2">
              {organizations.map((org) => (
                <li key={org.id}>
                  <Button
                    variant="outline"
                    className="h-auto w-full justify-start gap-3 px-3 py-3"
                    data-testid={`connect-device-option-${org.id}`}
                    onClick={() => void handlePick(org.id)}
                    disabled={submitting}
                  >
                    {org.avatarUrl ? (
                      <img
                        src={org.avatarUrl}
                        alt=""
                        className="size-8 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground">
                        {org.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                      <span className="truncate text-sm font-medium">{org.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{org.slug}</span>
                    </div>
                    {submittingOrgId === org.id && (
                      <span className="text-xs text-muted-foreground">Connecting...</span>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {submitError && (
            <Alert variant="destructive" data-testid="connect-device-submit-error">
              <AlertCircle className="size-4" />
              <AlertTitle>Could not finish connecting</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
